import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  Typography,
  OutlinedInput,
  SelectChangeEvent,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
// [Web UX 9 / T2, 2026-10-13] a dátumválasztók magyar feliratai (Cancel/OK/Select date helyett) — lásd utils/dateLocale.ts
import { HU_PICKER_LOCALE_TEXT } from '../../utils/dateLocale';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { hu } from 'date-fns/locale';
import coachAvailabilityService, {
  UpdateCoachAvailabilityDto,
  CoachAvailabilityDto,
  VenueDto
} from '../../services/coachAvailabilityService';
import eventService from '../../services/eventService';
import { useFeedback } from '../../contexts/FeedbackContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import { useUnsavedChanges } from '../../contexts/UnsavedChangesContext';
import { isFormDirty, discardChangesConfirm } from '../../utils/formDirty';
import { requestErrorMessage, saveErrorMessage } from '../../utils/saveErrors';
import { toHourMinute, formatDateToLocal } from '../../utils/dateUtils';

interface CoachAvailabilityEditDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  availabilityId: string | null;
}

const CoachAvailabilityEditDialog: React.FC<CoachAvailabilityEditDialogProps> = ({
  open,
  onClose,
  onSuccess,
  availabilityId
}) => {
  const [formData, setFormData] = useState<UpdateCoachAvailabilityDto>({
    // Helyi nap, nem UTC [NB-6, 2026-09-13]: a `toISOString()` 00:00–02:00 budapesti idő között
    // a TEGNAPI napot adja. (Betöltés után amúgy felülírja a szerver adata.)
    availabilityDate: formatDateToLocal(new Date()),
    startTime: '09:00',
    endTime: '17:00',
    notes: '',
    venueIds: [],
    isActive: true
  });
  const [venues, setVenues] = useState<VenueDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState<string>('');
  const [availability, setAvailability] = useState<CoachAvailabilityDto | null>(null);
  /** A BETÖLTÖTT elérhetőség, mint alapvonal; `null` = még nem töltött be → soha nem piszkos. */
  const [baseline, setBaseline] = useState<UpdateCoachAvailabilityDto | null>(null);
  const { showSuccess, showError } = useFeedback();
  const confirm = useConfirm();

  // `open &&`: a dialógus zárva is mountolva marad a szülőben, az utolsó tartalommal.
  const isDirty = open && isFormDirty(baseline, formData);
  useUnsavedChanges(isDirty, 'Edzői elérhetőség');

  useEffect(() => {
    if (open && availabilityId) {
      loadAvailabilityData();
      loadVenues();
    }
  }, [open, availabilityId]);

  const loadAvailabilityData = async () => {
    if (!availabilityId) return;

    try {
      setLoadingData(true);
      // Betöltés közben nincs mentetlen adat (és az előző elérhetőség alapvonala már nem érvényes).
      setBaseline(null);
      const data = await coachAvailabilityService.getCoachAvailability(availabilityId);
      setAvailability(data);
      const loaded: UpdateCoachAvailabilityDto = {
        availabilityDate: data.availabilityDate,
        // ⚠️ „HH:mm"-re hozva [BL-2, 2026-09-13]: a szerver `TimeSpan`-ja „09:00:00" (mérve:
        // `GET /api/coach-availability/{id}`), az idő-választó viszont „09:00"-t ír vissza
        // (`toTimeString().slice(0, 5)`) — a nyers alakot alapvonalnak hagyva az eredeti idő
        // VISSZAÁLLÍTÁSA után is piszkos maradt az űrlap (09:00→10:00→09:00). A `formDirty`
        // idő-egyenértékűsége ezt önmagában is elfedi; ez a KETTŐS védelem másik fele.
        // A mentés így is „HH:mm"-et küld — ugyanazt, amit a létrehozó űrlap (élesben működik).
        startTime: toHourMinute(data.startTime),
        endTime: toHourMinute(data.endTime),
        notes: data.notes || '',
        venueIds: data.venues.map(v => v.venueId),
        isActive: data.isActive
      };
      setFormData(loaded);
      // Alapvonal a betöltés UTÁN, pontosan az űrlapba tett objektummal.
      setBaseline(loaded);
    } catch (err) {
      console.error('Error loading availability data:', err);
      // [Web UX 3. csomag (D), 2026-10-04] betöltési / nem-űrlap hiba: a lejárat-tudatos `requestErrorMessage` (a fájl mentetlen-
      // védelmes, ezért a közvetlen `apiErrorMessage` itt tiltott — `unsavedCoverage.test.ts`).
      setError(requestErrorMessage(err, 'Hiba történt az elérhetőség betöltésekor'));
    } finally {
      setLoadingData(false);
    }
  };

  const loadVenues = async () => {
    try {
      const venueData = await eventService.getVenues();
      // Védelem a formátum-eltérésre: a service korábban a `{success,data}` burkolót adta
      // tovább, amin a `.filter` TypeError-t dobott → a dialógus MINDIG hibát mutatott.
      // (A create-dialógus és a TableReservationDialog már így őrködik.)
      if (!Array.isArray(venueData)) {
        console.error('Venue data is not an array:', venueData);
        setVenues([]);
        setError('Hiba történt a helyszínek betöltésekor: nem megfelelő adatformátum');
        return;
      }
      setVenues(venueData.filter((venue: any) => venue.isActive));
    } catch (err) {
      console.error('Error loading venues:', err);
      setError(requestErrorMessage(err, 'Hiba történt a helyszínek betöltésekor'));
    }
  };

  const handleDateChange = (date: any) => {
    if (date) {
      const dateObj = date.$d || date;
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');
      setFormData(prev => ({
        ...prev,
        availabilityDate: `${year}-${month}-${day}`
      }));
    }
  };

  const handleStartTimeChange = (time: any) => {
    if (time) {
      const timeObj = time.$d || time;
      const timeString = timeObj.toTimeString().slice(0, 5);
      setFormData(prev => ({ ...prev, startTime: timeString }));
    }
  };

  const handleEndTimeChange = (time: any) => {
    if (time) {
      const timeObj = time.$d || time;
      const timeString = timeObj.toTimeString().slice(0, 5);
      setFormData(prev => ({ ...prev, endTime: timeString }));
    }
  };

  const handleVenueChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      venueIds: typeof value === 'string' ? value.split(',') : value
    }));
  };

  const handleNotesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, notes: event.target.value }));
  };

  const handleActiveChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, isActive: event.target.checked }));
  };

  const validateForm = (): boolean => {
    if (!formData.availabilityDate) {
      setError('Kérjük válasszon dátumot');
      return false;
    }
    if (!formData.startTime) {
      setError('Kérjük adja meg a kezdési időt');
      return false;
    }
    if (!formData.endTime) {
      setError('Kérjük adja meg a befejezési időt');
      return false;
    }
    if (formData.startTime >= formData.endTime) {
      setError('A befejezési idő később kell legyen, mint a kezdési idő');
      return false;
    }
    if (formData.venueIds.length === 0) {
      setError('Kérjük válasszon legalább egy helyszínt');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!availabilityId || !validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      await coachAvailabilityService.updateCoachAvailability(availabilityId, formData);
      showSuccess('Az elérhetőség módosítva.');
      onSuccess();
      closeWithoutAsking();
    } catch (err: any) {
      console.error('Error updating coach availability:', err);
      // [Web 7 / T3, 2026-10-06] Az elérhetőséget a szerkesztő NYITVA tartása alatt TÖRÖLTÉK (lelet: handoff
      // 4e). A backend 409-et ad a saját magyar mondatával, és semmit nem ír (`CoachAvailabilityController.cs`
      // `UpdateCoachAvailability`, „TÖRÖLT elérhetőség nem írható vissza"). A módosítás NEM menthető: KÉRDÉS
      // NÉLKÜL zárunk (a mentetlen-védelem itt hazudna), a szerver mondata toastban látszik, és a szülő
      // újratölt (`onSuccess` = a szülőkben „zárás + lista újratöltése"), hogy a törölt sor eltűnjön.
      // A `PUT /coach-availability/{id}` más 409-et nem ad.
      if (err?.response?.status === 409) {
        showError(saveErrorMessage(err, 'Ezt az elérhetőséget időközben törölték, a módosítás nem menthető.'));
        onSuccess();
        closeWithoutAsking();
        return;
      }
      // [validátor M-1 / 3. javító kör F-1, 2026-10-03] lejárt munkamenet → az újra-belépő ablakkal
      // egyező mondat; a közös, zár-tudatos burkolón át (`utils/saveErrors.ts`).
      setError(saveErrorMessage(err, 'Hiba történt az elérhetőség frissítésekor'));
    } finally {
      setLoading(false);
    }
  };

  /** Kérdés NÉLKÜLI zárás — a SIKERES mentés használja (a névről lásd NB-5 a create-dialógusban). */
  const closeWithoutAsking = () => {
    setFormData({
      // Helyi nap, nem UTC [NB-6]: a `toISOString()` 00:00–02:00 budapesti idő között a TEGNAPI
      // napot adja.
      availabilityDate: formatDateToLocal(new Date()),
      startTime: '09:00',
      endTime: '17:00',
      notes: '',
      venueIds: [],
      isActive: true
    });
    setError('');
    setAvailability(null);
    setBaseline(null);
    onClose();
  };

  /**
   * A FELHASZNÁLÓ záró útjai (a „Mégse" gomb és a Dialog `onClose`-a: backdrop + ESC).
   * A `closeWithoutAsking` maga kérdés NÉLKÜL zár — azt a SIKERES mentés használja.
   */
  const handleClose = async () => {
    if (isDirty && !(await confirm(discardChangesConfirm('elérhetőség űrlapja')))) {
      return;
    }
    closeWithoutAsking();
  };

  const parseTime = (timeString: string): Date => {
    const [hours, minutes] = timeString.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
  };

  const parseDate = (dateString: string): Date => {
    return new Date(dateString + 'T00:00:00');
  };

  if (loadingData) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={hu} localeText={HU_PICKER_LOCALE_TEXT}>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          Edző Elérhetőség Szerkesztése
          {availability && (
            <Typography variant="body2" color="text.secondary">
              {availability.coachName}
            </Typography>
          )}
        </DialogTitle>

        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <DatePicker
              label="Dátum"
              value={parseDate(formData.availabilityDate)}
              onChange={handleDateChange}
              slotProps={{
                textField: {
                  fullWidth: true,
                  variant: 'outlined'
                }
              }}
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TimePicker
                label="Kezdési idő"
                value={parseTime(formData.startTime)}
                onChange={handleStartTimeChange}
                ampm={false}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: 'outlined'
                  }
                }}
              />

              <TimePicker
                label="Befejezési idő"
                value={parseTime(formData.endTime)}
                onChange={handleEndTimeChange}
                ampm={false}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: 'outlined'
                  }
                }}
              />
            </Box>

            <FormControl fullWidth>
              <InputLabel>Helyszínek</InputLabel>
              <Select
                multiple
                value={formData.venueIds}
                onChange={handleVenueChange}
                input={<OutlinedInput label="Helyszínek" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => {
                      const venue = venues.find(v => v.venueId === value);
                      return (
                        <Chip
                          key={value}
                          label={venue?.name || value}
                          size="small"
                        />
                      );
                    })}
                  </Box>
                )}
              >
                {venues.map((venue) => (
                  <MenuItem key={venue.venueId} value={venue.venueId}>
                    {venue.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Megjegyzések (opcionális)"
              multiline
              rows={3}
              value={formData.notes}
              onChange={handleNotesChange}
              fullWidth
              variant="outlined"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={handleActiveChange}
                />
              }
              label="Aktív"
            />

            <Typography variant="body2" color="text.secondary">
              * Az elérhetőség azt jelzi, hogy az edző elérhető edzések szervezéséhez a megadott időpontban és helyszínen/helyszíneken.
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Mégse
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : null}
          >
            {loading ? 'Mentés...' : 'Mentés'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default CoachAvailabilityEditDialog;